import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  NotificationService,
  NotificationData,
} from '../../../core/notification.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-notification-center',
  standalone: true,
  imports: [CommonModule],
  template: `
    <!-- Notification Bell -->
    <div class="relative">
      <button
        (click)="toggleNotifications()"
        class="relative p-2 text-gray-600 hover:text-gray-800 transition-colors"
        [class.text-blue-600]="unreadCount > 0"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>

        <!-- Badge de contador -->
        <span
          *ngIf="unreadCount > 0"
          class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center animate-pulse"
        >
          {{ unreadCount > 99 ? '99+' : unreadCount }}
        </span>
      </button>

      <!-- Dropdown de Notificações -->
      <div
        *ngIf="showNotifications"
        class="absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl border border-gray-100 z-50 max-h-96 flex flex-col"
        (click)="$event.stopPropagation()"
      >
        <!-- Header -->
        <div
          class="p-4 border-b border-gray-100 flex items-center justify-between"
        >
          <h3 class="text-lg font-semibold text-gray-900">Notificações</h3>
          <div class="flex gap-2">
            <button
              *ngIf="unreadCount > 0"
              (click)="markAllAsRead()"
              class="text-sm text-blue-600 hover:text-blue-700 transition-colors"
            >
              Marcar todas como lidas
            </button>
            <button
              (click)="closeNotifications()"
              class="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        <!-- Lista de Notificações -->
        <div class="flex-1 overflow-y-auto">
          <div
            *ngIf="notifications.length === 0"
            class="p-6 text-center text-gray-500"
          >
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1"
              class="mx-auto mb-3 text-gray-300"
            >
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            <p class="text-sm">Nenhuma notificação</p>
          </div>

          <div
            *ngFor="
              let notification of notifications;
              trackBy: trackByNotificationId
            "
            class="border-b border-gray-50 last:border-b-0"
          >
            <div
              class="p-4 hover:bg-gray-50 transition-colors cursor-pointer relative"
              [class.bg-blue-50]="!notification.read"
              (click)="handleNotificationClick(notification)"
            >
              <!-- Indicador de não lida -->
              <div
                *ngIf="!notification.read"
                class="absolute left-2 top-1/2 transform -translate-y-1/2 w-2 h-2 bg-blue-500 rounded-full"
              ></div>

              <div
                class="flex items-start space-x-3"
                [class.ml-4]="!notification.read"
              >
                <!-- Ícone da categoria -->
                <div
                  class="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                  [ngClass]="{
                    'bg-blue-100 text-blue-600':
                      notification.category === 'info',
                    'bg-green-100 text-green-600':
                      notification.category === 'success',
                    'bg-yellow-100 text-yellow-600':
                      notification.category === 'warning',
                    'bg-red-100 text-red-600': notification.category === 'error'
                  }"
                >
                  <svg
                    *ngIf="notification.category === 'info'"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  <svg
                    *ngIf="notification.category === 'success'"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <polyline points="20,6 9,17 4,12" />
                  </svg>
                  <svg
                    *ngIf="notification.category === 'warning'"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                    />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  <svg
                    *ngIf="notification.category === 'error'"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                </div>

                <!-- Conteúdo -->
                <div class="flex-1 min-w-0">
                  <h4 class="text-sm font-medium text-gray-900 truncate">
                    {{ notification.title }}
                  </h4>
                  <p class="text-sm text-gray-600 mt-0.5 line-clamp-2">
                    {{ notification.body }}
                  </p>
                  <p class="text-xs text-gray-400 mt-1">
                    {{ formatTime(notification.timestamp) }}
                  </p>
                </div>

                <!-- Ações -->
                <div class="flex-shrink-0 flex items-center space-x-1">
                  <button
                    *ngIf="!notification.read"
                    (click)="markAsRead(notification.id, $event)"
                    class="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                    title="Marcar como lida"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <polyline points="20,6 9,17 4,12" />
                    </svg>
                  </button>
                  <button
                    (click)="deleteNotification(notification.id, $event)"
                    class="p-1 text-gray-400 hover:text-red-600 transition-colors"
                    title="Excluir"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <polyline points="3,6 5,6 21,6" />
                      <path
                        d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div
          *ngIf="notifications.length > 0"
          class="p-3 border-t border-gray-100 bg-gray-50 rounded-b-xl"
        >
          <div class="flex justify-between">
            <button
              (click)="clearAll()"
              class="text-sm text-red-600 hover:text-red-700 transition-colors"
            >
              Limpar todas
            </button>
            <div class="flex gap-2">
              <button
                (click)="sendTestNotification()"
                class="text-sm text-gray-600 hover:text-gray-700 transition-colors"
              >
                Teste
              </button>
              <button
                (click)="sendUrgentNotification()"
                class="text-sm text-red-600 hover:text-red-700 transition-colors"
              >
                Urgente
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Overlay para fechar ao clicar fora -->
    <div
      *ngIf="showNotifications"
      class="fixed inset-0 z-40"
      (click)="closeNotifications()"
    ></div>
  `,
  styles: [
    `
      .line-clamp-2 {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      @media (max-width: 640px) {
        .notification-dropdown {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          width: 100%;
          max-width: 100%;
          height: 100%;
          max-height: 100%;
          border-radius: 0;
          margin: 0;
        }
      }
    `,
  ],
})
export class NotificationCenterComponent implements OnInit, OnDestroy {
  private notificationService = inject(NotificationService);
  private destroy$ = new Subject<void>();

  showNotifications = false;
  notifications: NotificationData[] = [];
  unreadCount = 0;

  ngOnInit(): void {
    // Subscribe to notifications
    this.notificationService.notifications$
      .pipe(takeUntil(this.destroy$))
      .subscribe((notifications) => {
        this.notifications = notifications;
      });

    // Subscribe to unread count
    this.notificationService.unreadCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => {
        this.unreadCount = count;
      });

    // Close notifications when clicking outside
    document.addEventListener(
      'click',
      this.handleOutsideClick.bind(this),
      true
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    document.removeEventListener(
      'click',
      this.handleOutsideClick.bind(this),
      true
    );
  }

  private handleOutsideClick(event: Event): void {
    const target = event.target as HTMLElement;
    const notificationElement = target.closest('.notification-dropdown');
    const buttonElement = target.closest('button');

    if (!notificationElement && !buttonElement && this.showNotifications) {
      this.showNotifications = false;
    }
  }

  toggleNotifications(): void {
    this.showNotifications = !this.showNotifications;
  }

  closeNotifications(): void {
    this.showNotifications = false;
  }

  markAsRead(notificationId: string, event: Event): void {
    event.stopPropagation();
    this.notificationService.markAsRead(notificationId);
  }

  markAllAsRead(): void {
    this.notificationService.markAllAsRead();
  }

  deleteNotification(notificationId: string, event: Event): void {
    event.stopPropagation();
    this.notificationService.deleteNotification(notificationId);
  }

  clearAll(): void {
    if (confirm('Tem certeza que deseja limpar todas as notificações?')) {
      this.notificationService.clearAllNotifications();
    }
  }

  handleNotificationClick(notification: NotificationData): void {
    // Mark as read when clicked
    if (!notification.read) {
      this.notificationService.markAsRead(notification.id);
    }

    // Handle different notification types
    if (notification.data) {
      switch (notification.data.type) {
        case 'stock-alert':
          // Navigate to products with low stock
          console.log('Navigate to stock alerts');
          break;
        case 'sale':
          // Navigate to sales detail
          console.log('Navigate to sale detail');
          break;
        case 'order':
          // Navigate to order detail
          console.log('Navigate to order detail');
          break;
        case 'report':
          // Navigate to reports
          console.log('Navigate to reports');
          break;
        default:
          console.log('Default notification action');
      }
    }

    this.closeNotifications();
  }

  formatTime(timestamp: number): string {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) {
      return 'Agora mesmo';
    } else if (minutes < 60) {
      return `${minutes}m atrás`;
    } else if (hours < 24) {
      return `${hours}h atrás`;
    } else if (days < 7) {
      return `${days}d atrás`;
    } else {
      return new Date(timestamp).toLocaleDateString('pt-BR');
    }
  }

  trackByNotificationId(index: number, notification: NotificationData): string {
    return notification.id;
  }

  // Demo methods
  sendTestNotification(): void {
    this.notificationService.sendTestNotification();
  }

  sendUrgentNotification(): void {
    this.notificationService.sendUrgentNotification();
  }
}
